import { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  Card,
  Col,
  Collapse,
  Form,
  OverlayTrigger,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
} from "react-bootstrap";
import {
  avgYearlyDrawDown,
  sharpeRatio,
  tenYearsReturnH,
  turnover,
} from "../ProductListNew";

type RankSidebarProps = {
  handleChangeRank: (weights: any) => void;
  handleSwitchAbsPerf: (indicator: string, checked: boolean) => void;
};

const RankSidebar = ({
  handleChangeRank,
  handleSwitchAbsPerf,
}: RankSidebarProps) => {
  const [showSliders, setShowSliders] = useState(true);

  const sliders = useMemo(
    () => [
      {
        label: "Sharpe Ratio",
        defaultValue: 0,
        indicator: sharpeRatio,
        hasCheckbox: false,
        popoverDescription:
          "Return generated per unit of total risk (volatility). A higher Sharpe ratio indicates more efficient risk-adjusted performance.",
      },
      {
        label: "Low Risk",
        defaultValue: 0,
        indicator: avgYearlyDrawDown,
        hasCheckbox: false,
        popoverDescription:
          "Prioritizes strategies with lower volatility and drawdown characteristics, reducing the magnitude of potential losses.",
      },
      {
        label: "Long Term Performance",
        defaultValue: 0,
        indicator: tenYearsReturnH,
        hasCheckbox: true,
        onChangeCheckbox: handleSwitchAbsPerf,
        popoverDescription:
          "Annualized return over the full evaluation period. When Excess is selected, performance is measured relative to the benchmark.",
      },
      //   {
      //     label: "Short Term Performance",
      //     defaultValue: 0,
      //     indicator: oneYearReturn,
      //     hasCheckbox: true,
      //     onChangeCheckbox: handleSwitchAbsPerf,
      //   },
      {
        label: "Low Turnover",
        defaultValue: 0,
        indicator: turnover,
        hasCheckbox: false,
        popoverDescription:
          "Prioritizes strategies with lower portfolio positions turnover, minimizing transaction costs and implementation drag.",
      },
    ],
    [handleSwitchAbsPerf],
  );

  const defaultSlider = useMemo(() => {
    return sliders.reduce((prev: any, current) => {
      prev[current["indicator"]] = current["defaultValue"];
      return prev;
    }, {});
  }, [sliders]);

  const weightsRef = useRef<any>(defaultSlider);

  const onChangeValue = useCallback(
    ({ id, value }: { id: string; value: number }) => {
      const updatedWeights = { ...weightsRef.current, [id]: value };
      handleChangeRank(updatedWeights);

      weightsRef.current = updatedWeights;
    },
    [handleChangeRank],
  );

  const toggleSlidersVisibility = useCallback(() => {
    setShowSliders((current) => !current);
  }, []);

  return (
    <Card className="mb-3">
      <Card.Header
        onClick={toggleSlidersVisibility}
        style={{ cursor: "pointer" }}
      >
        Rank by relevance
      </Card.Header>
      <Collapse in={showSliders}>
        <Card.Body>
          <p>
            Adjust the sliders to boost the importance of each measure in
            shaping the final ranking.
          </p>
          <Sliders sliders={sliders} onChangeValue={onChangeValue} />
        </Card.Body>
      </Collapse>
    </Card>
  );
};

const Sliders = memo(({ sliders, onChangeValue }: any) => {
  return (
    <ul style={{ listStyle: "none", paddingLeft: 0 }}>
      {sliders.map((sl: any, idx: number) => (
        <li key={idx} style={{ marginBottom: "10px" }}>
          <SliderInput
            popoverDescription={sl.popoverDescription}
            hasCheckbox={sl.hasCheckbox}
            handleCheckboxChange={sl.onChangeCheckbox}
            sliderIndicator={sl.indicator}
            label={sl.label}
            defaultValue={sl.defaultValue}
            handleChange={onChangeValue}
          />
        </li>
      ))}
    </ul>
  );
});

const showRelevanceOnHover = (val: number) => {
  const LABELS_DICTIONARY: Record<string, string> = {
    "0.1": "Very low relevance",
    "0.2": "Low relevance",
    "0.3": "Slight relevance",
    "0.4": "Moderately low relevance",
    "0.5": "Medium relevance",
    "0.6": "Moderately high relevance",
    "0.7": "High relevance",
    "0.8": "Very high relevance",
    "0.9": "Extremely high relevance",
    "1": "Maximum relevance",
  };
  return LABELS_DICTIONARY[val.toFixed(1)] ?? "";
};

const SliderInput = ({
  label,
  handleChange,
  popoverDescription,
  hasCheckbox = false,
  defaultValue,
  sliderIndicator,
  handleCheckboxChange,
}: any) => {
  const [relevance, setRelevance] = useState(
    showRelevanceOnHover(defaultValue),
  );

  const changeValue = useCallback(
    (evt: any) => {
      const value = parseFloat(evt.target.value);
      handleChange({ id: sliderIndicator, value });
    },
    [handleChange, sliderIndicator],
  );

  const onChangeAbsCheckbox = useCallback(
    (evt: any) => {
      if (handleCheckboxChange != null) {
        handleCheckboxChange(sliderIndicator, evt.target.checked);
      }
    },
    [handleCheckboxChange, sliderIndicator],
  );

  const handleChangeValue = useCallback((evt: any) => {
    const value = parseFloat(evt.target.value);
    if (value > 0) {
      setRelevance(showRelevanceOnHover(value));
    } else {
      setRelevance("");
    }
  }, []);

  return (
    <Card className="p-2">
      <Row className="align-items-center">
        <Col>
          <Form.Label>
            <OverlayTrigger
              rootClose
              trigger="click"
              placement="right"
              overlay={
                <Popover
                  style={{ maxWidth: "25vw" }}
                  className="rounded"
                  id="popover-basic"
                  placement="right"
                >
                  <PopoverHeader>{label}</PopoverHeader>
                  <PopoverBody>{popoverDescription}</PopoverBody>
                </Popover>
              }
            >
              <p className="label--withInfoIcon">
                {label} <i className="bi bi-info-circle hoverable-info"></i>
              </p>
            </OverlayTrigger>
          </Form.Label>
          {hasCheckbox && (
            <Form.Check
              type="checkbox"
              label="Excess"
              onChange={onChangeAbsCheckbox}
              className="ms-2"
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Range
            min={0}
            max={1}
            step={0.2}
            defaultValue={defaultValue}
            onChange={(evt) => {
              changeValue(evt);
              handleChangeValue(evt);
            }}
          />
          {relevance !== "" && (
            <div style={{ textAlign: "right", fontSize: "0.8em" }}>
              {relevance}
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default RankSidebar;
